import { useState } from "react";
import type { AnyBlock, Block, BlockType } from "@/types";
import { BLOCK_META } from "./defaults";
import { BlockEditor } from "./BlockEditor";

const KNOWN = new Set<string>(Object.keys(BLOCK_META));

/** One row in the editor list: header (label, move, remove, expand) + the
 *  per-type property editor when expanded. */
export function BlockRow({
  block,
  index,
  count,
  onChange,
  onRemove,
  onMove,
}: {
  block: AnyBlock;
  index: number;
  count: number;
  onChange: (b: Block) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
}) {
  const [open, setOpen] = useState(false);
  const known = KNOWN.has(block.type);
  const label = known ? BLOCK_META[block.type as BlockType].label : `Unknown (${block.type})`;
  const hasBg = !!block.background && block.background.kind !== undefined;

  return (
    <li className="block-row">
      <div className="block-row__head">
        <button
          type="button"
          className="block-row__toggle"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
        >
          <span className="block-row__caret" aria-hidden="true">{open ? "▾" : "▸"}</span>
          <span className="block-row__label">{label}</span>
          {hasBg ? <span className="block-row__tag">bg</span> : null}
        </button>
        <div className="block-row__actions">
          <button
            type="button"
            className="block-row__btn"
            onClick={() => onMove(-1)}
            disabled={index === 0}
            aria-label="Move up"
          >
            ↑
          </button>
          <button
            type="button"
            className="block-row__btn"
            onClick={() => onMove(1)}
            disabled={index === count - 1}
            aria-label="Move down"
          >
            ↓
          </button>
          <button
            type="button"
            className="block-row__btn block-row__btn--danger"
            onClick={onRemove}
            aria-label="Remove block"
          >
            ✕
          </button>
        </div>
      </div>

      {open ? (
        known ? (
          <BlockEditor block={block as Block} onChange={onChange} />
        ) : (
          <p className="block-row__unknown">This block type isn’t editable in this version.</p>
        )
      ) : null}
    </li>
  );
}
